import {ANTIBODY_SCHEMA_INTERFACE_ID} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {pluralize} from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {TagSchemaSupportedFolderItemType} from 'constants/RegistryConstants';
import {TagSchemaItemType, TagSchemaSequenceSchemaType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EQUIPMENT_SCHEMA_INTERFACE_ID} from 'instruments/equipment/EquipmentConstants';
import {ComplexPolymerInterfaceApiId} from 'modbio/complex-polymers/ComplexPolymerSchemaInterfaceConstants';
import {SSI_ADC_INTERFACE_ID} from 'molbio/adcs/schema-configuration/AdcSchemaInterfaceConstants';
import {TRIAL_SUBJECT_SCHEMA_INTERFACE_ID} from 'registry/trial-interfaces/TrialInterfaceConstants';
import {assertNever} from 'util/Types';

// Keep in sync with ITEM_TYPE_TO_DISPLAY_NAME in benchling/schemas/tag_schemas/tag_schema_item_type_util.py.
export function getItemTypeDisplayName(itemType: TagSchemaItemType, plural: boolean = false): string {
  const itemCount = plural ? 2 : 1;
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return pluralize(itemCount, 'AA Sequence');
    case TagSchemaItemType.ADC:
      return pluralize(itemCount, 'Antibody-Drug Conjugate');
    case TagSchemaItemType.ANTIBODY:
      return pluralize(itemCount, 'Protein');
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
      return pluralize(itemCount, 'Custom Entity');
    case TagSchemaItemType.DNA_SEQUENCE:
      return pluralize(itemCount, 'DNA Sequence');
    case TagSchemaItemType.DNA_OLIGO:
      return pluralize(itemCount, 'DNA Oligo');
    case TagSchemaItemType.ENTRY:
      return pluralize(itemCount, 'Entry');
    case TagSchemaItemType.EQUIPMENT:
      return 'Equipment';
    case TagSchemaItemType.MIXED:
      return pluralize(itemCount, 'Sequence (DNA or Oligo)', 'Sequences (DNA or Oligo)');
    case TagSchemaItemType.MIXTURE:
      return pluralize(itemCount, 'Mixture');
    case TagSchemaItemType.MOLECULE:
      return pluralize(itemCount, 'Molecule');
    case TagSchemaItemType.OLIGO_CONJUGATE:
      return pluralize(itemCount, 'Oligo Conjugate');
    case TagSchemaItemType.OLIGO_DUPLEX:
      return pluralize(itemCount, 'Oligo Duplex');
    case TagSchemaItemType.RNA_OLIGO:
      return pluralize(itemCount, 'RNA Oligo');
    case TagSchemaItemType.RNA_SEQUENCE:
      return pluralize(itemCount, 'RNA Sequence');
    case TagSchemaItemType.TRIAL_SUBJECT:
      return pluralize(itemCount, 'Trial Subject');
    default:
      assertNever(itemType);
      return '(unknown)';
  }
}

// Temporary function to convert from new overall item type back to folderItemType and sequenceType
export function itemTypeToFolderItemTypeAndSequenceType(itemType: TagSchemaItemType): {
  folderItemType: TagSchemaSupportedFolderItemType;
  sequenceType: TagSchemaSequenceSchemaType | null;
} {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return {folderItemType: ModelTypeConstants.PROTEIN, sequenceType: null};
    case TagSchemaItemType.ADC:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.ANTIBODY:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.BASIC_FOLDER_ITEM:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.DNA_OLIGO:
      return {folderItemType: ModelTypeConstants.SEQUENCE, sequenceType: TagSchemaSequenceSchemaType.OLIGO};
    case TagSchemaItemType.DNA_SEQUENCE:
      return {
        folderItemType: ModelTypeConstants.SEQUENCE,
        sequenceType: TagSchemaSequenceSchemaType.DNA_SEQUENCE,
      };
    case TagSchemaItemType.ENTRY:
      return {folderItemType: ModelTypeConstants.ENTRY, sequenceType: null};
    case TagSchemaItemType.EQUIPMENT:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.MIXED:
      return {folderItemType: ModelTypeConstants.SEQUENCE, sequenceType: TagSchemaSequenceSchemaType.MIXED};
    case TagSchemaItemType.MIXTURE:
      return {folderItemType: ModelTypeConstants.MIXTURE, sequenceType: null};
    case TagSchemaItemType.MOLECULE:
      return {folderItemType: ModelTypeConstants.MOLECULE, sequenceType: null};
    case TagSchemaItemType.OLIGO_CONJUGATE:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.OLIGO_DUPLEX:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    case TagSchemaItemType.RNA_OLIGO:
      return {
        folderItemType: ModelTypeConstants.SEQUENCE,
        sequenceType: TagSchemaSequenceSchemaType.RNA_OLIGO,
      };
    case TagSchemaItemType.RNA_SEQUENCE:
      return {
        folderItemType: ModelTypeConstants.SEQUENCE,
        sequenceType: TagSchemaSequenceSchemaType.RNA_SEQUENCE,
      };
    case TagSchemaItemType.TRIAL_SUBJECT:
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
    default:
      assertNever(itemType);
      return {folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM, sequenceType: null};
  }
}

// Temporary convenience function to convert the deprecated folderItemType and sequenceType to itemType
export function folderItemTypeAndSequenceTypeToItemType(
  folderItemType: TagSchemaSupportedFolderItemType,
  sequenceType: TagSchemaSequenceSchemaType | null
): TagSchemaItemType {
  switch (folderItemType) {
    case ModelTypeConstants.PROTEIN:
      return TagSchemaItemType.AA_SEQUENCE;
    case ModelTypeConstants.BASIC_FOLDER_ITEM:
      return TagSchemaItemType.BASIC_FOLDER_ITEM;
    case ModelTypeConstants.ENTRY:
      return TagSchemaItemType.ENTRY;
    case ModelTypeConstants.MIXTURE:
      return TagSchemaItemType.MIXTURE;
    case ModelTypeConstants.MOLECULE:
      return TagSchemaItemType.MOLECULE;
    case ModelTypeConstants.SEQUENCE:
      if (sequenceType === TagSchemaSequenceSchemaType.DNA_SEQUENCE) {
        return TagSchemaItemType.DNA_SEQUENCE;
      } else if (sequenceType === TagSchemaSequenceSchemaType.OLIGO) {
        return TagSchemaItemType.DNA_OLIGO;
      } else if (sequenceType === TagSchemaSequenceSchemaType.RNA_SEQUENCE) {
        return TagSchemaItemType.RNA_SEQUENCE;
      } else if (sequenceType === TagSchemaSequenceSchemaType.RNA_OLIGO) {
        return TagSchemaItemType.RNA_OLIGO;
      } else if (sequenceType === TagSchemaSequenceSchemaType.MIXED) {
        return TagSchemaItemType.MIXED;
      } else {
        return TagSchemaItemType.DNA_SEQUENCE;
      }
    default:
      return TagSchemaItemType.BASIC_FOLDER_ITEM;
  }
}

const ITEM_TYPE_TO_SSI_ID: Record<TagSchemaItemType, string | null> = {
  [TagSchemaItemType.AA_SEQUENCE]: null,
  [TagSchemaItemType.BASIC_FOLDER_ITEM]: null,
  [TagSchemaItemType.DNA_OLIGO]: null,
  [TagSchemaItemType.DNA_SEQUENCE]: null,
  [TagSchemaItemType.RNA_OLIGO]: null,
  [TagSchemaItemType.RNA_SEQUENCE]: null,
  [TagSchemaItemType.ENTRY]: null,
  [TagSchemaItemType.MIXED]: null,
  [TagSchemaItemType.MIXTURE]: null,
  [TagSchemaItemType.MOLECULE]: null,
  [TagSchemaItemType.ADC]: SSI_ADC_INTERFACE_ID,
  [TagSchemaItemType.ANTIBODY]: ANTIBODY_SCHEMA_INTERFACE_ID,
  [TagSchemaItemType.OLIGO_CONJUGATE]: ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
  [TagSchemaItemType.OLIGO_DUPLEX]: ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
  [TagSchemaItemType.TRIAL_SUBJECT]: TRIAL_SUBJECT_SCHEMA_INTERFACE_ID,
  [TagSchemaItemType.EQUIPMENT]: EQUIPMENT_SCHEMA_INTERFACE_ID,
};

export const SSI_ID_TO_ITEM_TYPE: Record<string, TagSchemaItemType> = Object.fromEntries(
  Object.entries(ITEM_TYPE_TO_SSI_ID)
    .filter(([_, ssiId]) => ssiId != null)
    .map(([itemType, ssiId]) => [ssiId, itemType])
);

export function getStaticSchemaInterfaceIdFromItemType(itemType: TagSchemaItemType): string | null {
  return ITEM_TYPE_TO_SSI_ID[itemType] ?? null;
}

export function getItemTypeFromStaticSchemaInterfaceId(interfaceId: string): TagSchemaItemType | null {
  return SSI_ID_TO_ITEM_TYPE[interfaceId] ?? null;
}

export function showStaticSchemaInterfaceAsType(interfaceId: string): boolean {
  const exclusiveSSIIds = [
    ANTIBODY_SCHEMA_INTERFACE_ID,
    EQUIPMENT_SCHEMA_INTERFACE_ID,
    ComplexPolymerInterfaceApiId.OLIGO_CONJUGATE_ID,
    ComplexPolymerInterfaceApiId.OLIGO_DUPLEX_ID,
    TRIAL_SUBJECT_SCHEMA_INTERFACE_ID,
  ];
  return exclusiveSSIIds.includes(interfaceId);
}
